import React, { useState } from 'react';
import { X, Download, ChevronRight, ChevronLeft } from 'lucide-react';

interface BrandDetails {
  website: string;
  websiteNotApplicable: boolean;
  industryKeywords: string;
  targetAudience: string[];
  brandVoice: string;
}

interface ContentPreferences {
  category: string;
  format: string;
  length: string;
  style: string;
  instructions: string;
}

interface ContentType {
  contentType: string;
  contentTypeDescription: string;
}

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (brandDetails: BrandDetails, contentPreferences: ContentPreferences) => void;
  contentTypes: ContentType[];
}

const audienceOptions = [
  'Citizens',
  'Small business owners',
  'Government employees',
  'Students',
  'Senior citizens',
  'Rural communities',
  'Urban residents',
  'Women entrepreneurs'
];

export const ExportDialog: React.FC<ExportDialogProps> = ({ isOpen, onClose, onGenerate, contentTypes }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [newAudience, setNewAudience] = useState('');
  const [brandDetails, setBrandDetails] = useState<BrandDetails>({
    website: '',
    websiteNotApplicable: false,
    industryKeywords: '',
    targetAudience: [],
    brandVoice: ''
  });
  
  const [contentPreferences, setContentPreferences] = useState<ContentPreferences>({
    category: contentTypes.length > 0 ? contentTypes[0].contentType : '',
    format: 'Text Only',
    length: 'Medium (300-500 words)',
    style: 'Professional',
    instructions: ''
  });

  if (!isOpen) return null;

  const handleBrandDetailsChange = (field: keyof BrandDetails, value: string | boolean) => {
    setBrandDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleAudienceToggle = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.includes(audience)
        ? prev.targetAudience.filter(a => a !== audience)
        : [...prev.targetAudience, audience]
    }));
  };

  const handleAddCustomAudience = () => {
    if (newAudience.trim() && !brandDetails.targetAudience.includes(newAudience.trim())) {
      setBrandDetails(prev => ({
        ...prev,
        targetAudience: [...prev.targetAudience, newAudience.trim()]
      }));
      setNewAudience('');
    }
  };

  const handleRemoveAudience = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.filter(a => a !== audience)
    }));
  };

  const handleContentPreferenceChange = (field: keyof ContentPreferences, value: string) => {
    setContentPreferences(prev => ({ ...prev, [field]: value }));
  };

  const isContentLengthApplicable = () => {
    return contentPreferences.format === 'Text Only';
  };

  const handleNext = () => {
    if (currentStep < 2) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleGenerate = () => {
    onGenerate(brandDetails, contentPreferences);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Download className="w-6 h-6 text-[#4c3794]" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Export Content</h2>
              <p className="text-sm text-gray-500">Step {currentStep} of 2</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Brand Details</h3>
                <p className="text-sm text-gray-600">
                  These details help us create content that matches your brand voice and style.
                </p>
              </div>

              {/* Company Website */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Company Website
                </label>
                <input
                  type="url"
                  value={brandDetails.website}
                  onChange={(e) => handleBrandDetailsChange('website', e.target.value)}
                  disabled={brandDetails.websiteNotApplicable}
                  placeholder="https://www.example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                />
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={brandDetails.websiteNotApplicable}
                    onChange={(e) => setBrandDetails(prev => ({ ...prev, websiteNotApplicable: e.target.checked }))}
                    className="w-4 h-4 accent-[#4c3794]"
                  />
                  <span className="text-sm text-gray-600">Not applicable / No website</span>
                </label>
              </div>

              {/* Industry Keywords */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Industry Keywords
                </label>
                <input
                  type="text"
                  value={brandDetails.industryKeywords}
                  onChange={(e) => handleBrandDetailsChange('industryKeywords', e.target.value)}
                  placeholder="government services, public sector, citizen welfare"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Target Audience / Customer Segments
                </label>
                
                {/* Selected audiences */}
                {brandDetails.targetAudience.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {brandDetails.targetAudience.map((audience) => (
                      <span
                        key={audience}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-[#4c3794] text-sm rounded-full"
                      >
                        {audience}
                        <button
                          onClick={() => handleRemoveAudience(audience)}
                          className="hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Add custom audience */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newAudience}
                    onChange={(e) => setNewAudience(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomAudience()}
                    placeholder="Add custom audience"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                  />
                  <button
                    onClick={handleAddCustomAudience}
                    className="px-4 py-2 border border-[#4c3794] text-[#4c3794] rounded-lg hover:bg-[#4c3794] hover:text-white transition-colors"
                  >
                    Add
                  </button>
                </div>
                
                {/* Audience options */}
                <div className="flex flex-wrap gap-2">
                  {audienceOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleAudienceToggle(option)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        brandDetails.targetAudience.includes(option)
                          ? 'bg-purple-100 text-[#4c3794] border-[#4c3794]'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-[#4c3794]'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Voice */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Brand Voice & Tone
                </label>
                <textarea
                  value={brandDetails.brandVoice}
                  onChange={(e) => handleBrandDetailsChange('brandVoice', e.target.value)}
                  placeholder="bold, warm, authoritative, casual, formal, friendly, professional"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe your brand's voice and tone. You can select specific tones for each content piece during generation.
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Content Preferences</h3>
                <p className="text-sm text-gray-600">
                  Choose your preferred content format and style.
                </p>
              </div>

              {/* Content Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Content Category
                </label>
                <select
                  value={contentPreferences.category}
                  onChange={(e) => handleContentPreferenceChange('category', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                >
                  {contentTypes.map((contentType) => (
                    <option key={contentType.contentType} value={contentType.contentType}>
                      {contentType.contentTypeDescription}
                    </option>
                  ))}
                </select>
              </div>

              {/* Content Format */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Content Format
                </label>
                <select
                  value={contentPreferences.format}
                  onChange={(e) => handleContentPreferenceChange('format', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                >
                  <option value="Text Only">Text Only</option>
                  <option value="Image Only">Image Only</option>
                  <option value="Infographic">Infographic</option>
                  <option value="Video">Video</option>
                </select>
              </div>

              {/* Content Length */}
              {isContentLengthApplicable() && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Content Length
                  </label>
                  <select
                    value={contentPreferences.length}
                    onChange={(e) => handleContentPreferenceChange('length', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                  >
                    <option value="Short (100-200 words)">Short (100-200 words)</option>
                    <option value="Medium (300-500 words)">Medium (300-500 words)</option>
                    <option value="Long (600+ words)">Long (600+ words)</option>
                  </select>
                </div>
              )}

              {/* Writing Style */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Writing Style
                </label>
                <select
                  value={contentPreferences.style}
                  onChange={(e) => handleContentPreferenceChange('style', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent"
                >
                  <option value="Professional">Professional</option>
                  <option value="Casual">Casual</option>
                  <option value="Technical">Technical</option>
                  <option value="Creative">Creative</option>
                </select>
              </div>

              {/* Additional Instructions */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Additional Instructions (Optional)
                </label>
                <textarea
                  value={contentPreferences.instructions}
                  onChange={(e) => handleContentPreferenceChange('instructions', e.target.value)}
                  placeholder="Don't use emojis, don't use em-dash, avoid technical jargon, include call-to-action..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4c3794] focus:border-transparent resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrev}
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#4c3794] text-[#4c3794] rounded-lg hover:bg-purple-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
            )}
            
            {currentStep < 2 ? (
              <button
                onClick={handleNext}
                className="inline-flex items-center gap-2 px-6 py-2 bg-[#4c3794] text-white rounded-lg hover:bg-[#3a2a73] transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 px-6 py-2 bg-[#4c3794] text-white rounded-lg hover:bg-[#3a2a73] transition-colors"
              >
                <Download className="w-4 h-4" />
                Generate Content
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

